/**
 * The GS1 link-type registry — shape, not content.
 *
 * The content is vendored at `packages/shared/data/linktypes.json` with a dated
 * version file beside it (spec §15.9, ruled 2026-08-03). This module carries only
 * the schema that validates it and the CURIE helpers everything else needs.
 *
 * **Why the data is not imported here.** `packages/shared` is consumed by
 * `apps/app`, a browser bundle, so nothing in `src/` may reach the filesystem —
 * the same constraint that moved `uuidv7` off `node:crypto`. The snapshot is
 * exposed as a package export subpath instead (`@packagepage/shared/data/…`), so
 * `packages/db`'s seeder and this package's tests read the file directly and the
 * browser never sees 14 KB of vocabulary it has no use for.
 *
 * Nothing reads the registry on the resolver's request path either. A scan
 * carries `?linkType=<CURIE>` and the answer is whatever the mirrored entry says;
 * the registry decides what may be *written*, in the API and the dashboard.
 */
import { z } from "zod";
/**
 * The namespace every GS1-defined link type carries. Custom org types live under
 * their own namespace (§4, Growth+), which is why this is a prefix rather than an
 * assumption baked into comparisons.
 */
export const LINK_TYPE_CURIE_PREFIX = "gs1:";
/**
 * The registry's keys: lowerCamelCase tokens such as `pip`, `recallStatus`.
 * Verified against all 60 entries of the vendored snapshot rather than assumed.
 */
const LINK_TYPE_TOKEN = /^[a-z][A-Za-z0-9]*$/;
/**
 * One entry of the registry.
 *
 * `status` is a closed set on purpose. If GS1 introduces a third value, a refresh
 * fails the snapshot test instead of quietly seeding a status `packages/db` has no
 * handling for — the failure is the notification.
 *
 * `restricted` marks types not freely assignable by a brand: `["resolver"]` on
 * `defaultLink` and `handledBy`, `["validatedHealthcare"]` on the regulated
 * medicinal types. It is the fact `link_types.is_default_eligible` and the
 * dashboard's type picker are derived from, so it is preserved rather than dropped.
 */
export const linkTypeDefinitionSchema = z.object({
    title: z.string().min(1),
    description: z.string().min(1),
    status: z.enum(["stable", "deprecated"]),
    restricted: z.array(z.string().min(1)).min(1).exactOptional(),
});
/** The whole vendored document: token → definition. */
export const linkTypeRegistrySchema = z.record(z.string().regex(LINK_TYPE_TOKEN), linkTypeDefinitionSchema);
/**
 * The version file that pins the snapshot.
 *
 * `sha256` and `bytes` exist because upstream is unversioned — a date alone cannot
 * tell you whether the bytes changed, and the whole point of vendoring is that the
 * vocabulary is a fact of a commit.
 */
export const linkTypeSnapshotVersionSchema = z.object({
    source: z.url(),
    /** ISO date. Day precision, because the upstream document has no finer version. */
    retrievedAt: z.iso.date(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    bytes: z.int().positive(),
    linkTypeCount: z.int().positive(),
    curiePrefix: z.literal(LINK_TYPE_CURIE_PREFIX),
    why: z.string().min(1),
});
/** `pip` → `gs1:pip`. */
export function curieForLinkType(token) {
    return `${LINK_TYPE_CURIE_PREFIX}${token}`;
}
/**
 * `gs1:pip` → `pip`; anything outside the GS1 namespace → `undefined`.
 *
 * Returns rather than throws because the caller is usually deciding whether a
 * CURIE is ours or a tenant's custom type, and that is a question, not an error.
 */
export function linkTypeTokenFromCurie(curie) {
    if (!curie.startsWith(LINK_TYPE_CURIE_PREFIX))
        return undefined;
    const token = curie.slice(LINK_TYPE_CURIE_PREFIX.length);
    return LINK_TYPE_TOKEN.test(token) ? token : undefined;
}
//# sourceMappingURL=linktypes.js.map